import type { FasciaSizeParams, RidgeRailAtPeakParams, RoofSlopesParams } from "building-types/framing/framingTypes";
import type { LumberProfileType } from "building-types/framing/lumberTypes";
import { LumberProfileCatalog } from "building-types/framing/lumberTypes";
import { TRUSS_THICKNESS } from "building-types/framing/roofFramingConstants";
import {
  RoofFramingPlaneType,
  type GeneratedRoofFramingPlane,
  type HipFramingMethod,
  type RoofFraming,
} from "building-types/framing/roofFramingTypes";
import type { RoofTrussBasicInput } from "building-types/framing/roofTrussTypes";
import { unsignedNumberNew } from "common/utils/nominalNumbers";
import { DIST_EPSILON } from "geom/core";
import { planeCopyReversed, planeMoveAlongNormal, planeNew, planeNormal } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import type { Vec3 } from "geom/vec3";
import { vec3Dist, vec3Normalize } from "geom/vec3";
import { calculateHipSpan } from "./hipFramingUtils";
import { generateJackRafter } from "./roofFramingUtils";

interface JackRaftersHipTrussesesParams {
  startHipFramingPlaneIndex: number | undefined;
  endHipFramingPlaneIndex: number | undefined;
  hipFramingMethod: HipFramingMethod;
  roofFraming: RoofFraming[];
  framingPlanes: GeneratedRoofFramingPlane[];
  framingPlaneThickness: number;
  peakRailExtendedNorm: [number, number, number];
  fasciaSizes: FasciaSizeParams;
  ridgeRailsAtPeak: RidgeRailAtPeakParams;
  jackRafterProfile: LumberProfileType;
  trussBasicInput: RoofTrussBasicInput;
  roofSlopes: RoofSlopesParams;
  roofFramingSpacing: number;
}

export function generateJackRaftersForHipTrusses(params: JackRaftersHipTrussesesParams) {
  if (params.startHipFramingPlaneIndex === undefined && params.endHipFramingPlaneIndex === undefined) {
    return;
  }
  const { leftTopChordPitch, rightTopChordPitch, startHipSideSlope, endHipSideSlope } = params.roofSlopes;
  const startHipJackPitch = startHipSideSlope ?? leftTopChordPitch ?? rightTopChordPitch;
  const endHipJackPitch = endHipSideSlope ?? leftTopChordPitch ?? rightTopChordPitch;

  const framingPlaneThickness = LumberProfileCatalog?.[params.jackRafterProfile]?.height ?? TRUSS_THICKNESS;
  if (
    params.startHipFramingPlaneIndex !== undefined &&
    params.ridgeRailsAtPeak.firstPeakFramingPlaneIndex !== undefined
  ) {
    const startHipFramingPlane = params.framingPlanes[params.startHipFramingPlaneIndex];
    const firstPeakFramingPlane = params.framingPlanes[params.ridgeRailsAtPeak.firstPeakFramingPlaneIndex];
    const startHipFramingPlaneOrigin = {
      x: firstPeakFramingPlane.plane.originX,
      y: firstPeakFramingPlane.plane.originY,
      z: firstPeakFramingPlane.plane.originZ,
    };
    const startTowardPlane = params.framingPlanes[0].plane;
    const reversedStartHipFramingPlane = planeCopyReversed(startHipFramingPlane.plane);
    const perpendicularSpan = vec3Dist(
      startHipFramingPlane.plane.originX,
      startHipFramingPlane.plane.originY,
      startHipFramingPlane.plane.originZ,
      startTowardPlane.originX,
      startTowardPlane.originY,
      startTowardPlane.originZ,
    );
    const commonParams = {
      peakOrigin: startHipFramingPlaneOrigin,
      indexOrigin: { x: startHipFramingPlane.plane.originX, y: startHipFramingPlane.plane.originY },
      trussPlane: startHipFramingPlane.plane,
      peakRailExtendedNorm: params.peakRailExtendedNorm,
    };
    const generateStartHipJackRafter = ({
      origin,
      plane,
      isLeftSide,
      rafterPlaneOffset = 0,
    }: {
      readonly origin: Vec3;
      readonly plane: Plane;
      readonly isLeftSide: boolean;
      readonly rafterPlaneOffset?: number;
    }): void => {
      generateJackRafterAt(
        { x: origin[0], y: origin[1], z: origin[2] },
        perpendicularSpan,
        startHipJackPitch,
        plane,
        true,
        isLeftSide,
        params.jackRafterProfile,
        params.fasciaSizes.startEaveFasciaSize,
        rafterPlaneOffset,
        params.roofFraming,
      );
    };
    if (params.ridgeRailsAtPeak.leftRidgeRailAtFirstPeak && !params.ridgeRailsAtPeak.rightRidgeRailAtFirstPeak) {
      const { span: leftHipSpan, intersectionVec3: intersectionVecLeftRidgeRail } = calculateHipSpan({
        ...commonParams,
        ridgeRail: params.ridgeRailsAtPeak.leftRidgeRailAtFirstPeak,
      });
      if (intersectionVecLeftRidgeRail && leftHipSpan) {
        generateStartHipJackRafter({
          origin: intersectionVecLeftRidgeRail,
          plane: reversedStartHipFramingPlane,
          isLeftSide: true,
        });
      }
    }
    if (params.ridgeRailsAtPeak.rightRidgeRailAtFirstPeak && !params.ridgeRailsAtPeak.leftRidgeRailAtFirstPeak) {
      const { span: rightHipSpan, intersectionVec3: intersectionVecRightRidgeRail } = calculateHipSpan({
        ...commonParams,
        ridgeRail: params.ridgeRailsAtPeak.rightRidgeRailAtFirstPeak,
      });
      if (intersectionVecRightRidgeRail && rightHipSpan) {
        generateStartHipJackRafter({
          origin: intersectionVecRightRidgeRail,
          plane: startHipFramingPlane.plane,
          isLeftSide: false,
        });
      }
    }
    if (params.ridgeRailsAtPeak.leftRidgeRailAtFirstPeak && params.ridgeRailsAtPeak.rightRidgeRailAtFirstPeak) {
      const { span: leftHipSpan, intersectionVec3: intersectionVecLeftRidgeRail } = calculateHipSpan({
        ...commonParams,
        ridgeRail: params.ridgeRailsAtPeak.leftRidgeRailAtFirstPeak,
      });
      const { span: rightHipSpan, intersectionVec3: intersectionVecRightRidgeRail } = calculateHipSpan({
        ...commonParams,
        ridgeRail: params.ridgeRailsAtPeak.rightRidgeRailAtFirstPeak,
      });
      if (intersectionVecLeftRidgeRail && leftHipSpan && intersectionVecRightRidgeRail && rightHipSpan) {
        const [jackRafterLeftOriginX, jackRafterLeftOriginY, jackRafterLeftOriginZ] = intersectionVecLeftRidgeRail;
        const [jackRafterRightOriginX, jackRafterRightOriginY, jackRafterRightOriginZ] = intersectionVecRightRidgeRail;
        const {
          left: leftOrigin,
          right: rightOrigin,
          middle: middleOrigin,
        } = computeJackRafterPositions(
          jackRafterLeftOriginX,
          jackRafterLeftOriginY,
          jackRafterLeftOriginZ,
          jackRafterRightOriginX,
          jackRafterRightOriginY,
          jackRafterRightOriginZ,
          params.roofFramingSpacing,
        );
        leftOrigin.forEach((position) => {
          generateStartHipJackRafter({
            origin: position,
            plane: reversedStartHipFramingPlane,
            isLeftSide: true,
          });
        });
        rightOrigin.forEach((position) => {
          generateStartHipJackRafter({
            origin: position,
            plane: startHipFramingPlane.plane,
            isLeftSide: false,
          });
        });
        if (middleOrigin) {
          generateStartHipJackRafter({
            origin: middleOrigin,
            plane: startHipFramingPlane.plane,
            isLeftSide: false,
            rafterPlaneOffset: -framingPlaneThickness / 2,
          });
        }
      }
    }
  }
  //do the same for the last peak1
  if (params.endHipFramingPlaneIndex !== undefined && params.ridgeRailsAtPeak.lastPeakFramingPlaneIndex !== undefined) {
    const endHipFramingPlane = params.framingPlanes[params.endHipFramingPlaneIndex];
    const lastPeakFramingPlane = params.framingPlanes[params.ridgeRailsAtPeak.lastPeakFramingPlaneIndex];
    const endHipFramingPlaneOrigin = {
      x: lastPeakFramingPlane.plane.originX,
      y: lastPeakFramingPlane.plane.originY,
      z: lastPeakFramingPlane.plane.originZ,
    };
    const endTowardPlane = params.framingPlanes[params.framingPlanes.length - 1].plane;
    const reversedEndHipFramingPlane = planeCopyReversed(endHipFramingPlane.plane);
    const perpendicularSpan = vec3Dist(
      endHipFramingPlane.plane.originX,
      endHipFramingPlane.plane.originY,
      endHipFramingPlane.plane.originZ,
      endTowardPlane.originX,
      endTowardPlane.originY,
      endTowardPlane.originZ,
    );
    const commonParams = {
      peakOrigin: endHipFramingPlaneOrigin,
      indexOrigin: { x: endHipFramingPlane.plane.originX, y: endHipFramingPlane.plane.originY },
      trussPlane: endHipFramingPlane.plane,
      peakRailExtendedNorm: params.peakRailExtendedNorm,
    };
    const generateEndHipJackRafter = ({
      origin,
      plane,
      isLeftSide,
      rafterPlaneOffset = 0,
    }: {
      readonly origin: Vec3;
      readonly plane: Plane;
      readonly isLeftSide: boolean;
      readonly rafterPlaneOffset?: number;
    }): void => {
      generateJackRafterAt(
        { x: origin[0], y: origin[1], z: origin[2] },
        perpendicularSpan,
        endHipJackPitch,
        plane,
        true,
        isLeftSide,
        params.jackRafterProfile,
        params.fasciaSizes.endEaveFasciaSize,
        rafterPlaneOffset,
        params.roofFraming,
      );
    };
    if (params.ridgeRailsAtPeak.leftRidgeRailAtLastPeak && !params.ridgeRailsAtPeak.rightRidgeRailAtLastPeak) {
      const { span: leftHipSpan, intersectionVec3: intersectionVecLeftRidgeRail } = calculateHipSpan({
        ...commonParams,
        ridgeRail: params.ridgeRailsAtPeak.leftRidgeRailAtLastPeak,
      });
      if (intersectionVecLeftRidgeRail && leftHipSpan) {
        generateEndHipJackRafter({
          origin: intersectionVecLeftRidgeRail,
          plane: reversedEndHipFramingPlane,
          isLeftSide: false,
        });
      }
    }
    if (params.ridgeRailsAtPeak.rightRidgeRailAtLastPeak && !params.ridgeRailsAtPeak.leftRidgeRailAtLastPeak) {
      const { span: rightHipSpan, intersectionVec3: intersectionVecRightRidgeRail } = calculateHipSpan({
        ...commonParams,
        ridgeRail: params.ridgeRailsAtPeak.rightRidgeRailAtLastPeak,
      });
      if (intersectionVecRightRidgeRail && rightHipSpan) {
        generateEndHipJackRafter({
          origin: intersectionVecRightRidgeRail,
          plane: endHipFramingPlane.plane,
          isLeftSide: true,
        });
      }
    }
    if (params.ridgeRailsAtPeak.leftRidgeRailAtLastPeak && params.ridgeRailsAtPeak.rightRidgeRailAtLastPeak) {
      const { span: leftHipSpan, intersectionVec3: intersectionVecLeftRidgeRail } = calculateHipSpan({
        ...commonParams,
        ridgeRail: params.ridgeRailsAtPeak.leftRidgeRailAtLastPeak,
      });
      const { span: rightHipSpan, intersectionVec3: intersectionVecRightRidgeRail } = calculateHipSpan({
        ...commonParams,
        ridgeRail: params.ridgeRailsAtPeak.rightRidgeRailAtLastPeak,
      });
      if (intersectionVecLeftRidgeRail && leftHipSpan && intersectionVecRightRidgeRail && rightHipSpan) {
        const [jackRafterLeftOriginX, jackRafterLeftOriginY, jackRafterLeftOriginZ] = intersectionVecLeftRidgeRail;
        const [jackRafterRightOriginX, jackRafterRightOriginY, jackRafterRightOriginZ] = intersectionVecRightRidgeRail;
        const {
          left: leftOrigin,
          right: rightOrigin,
          middle: middleOrigin,
        } = computeJackRafterPositions(
          jackRafterLeftOriginX,
          jackRafterLeftOriginY,
          jackRafterLeftOriginZ,
          jackRafterRightOriginX,
          jackRafterRightOriginY,
          jackRafterRightOriginZ,
          params.roofFramingSpacing,
        );
        leftOrigin.forEach((position) => {
          generateEndHipJackRafter({
            origin: position,
            plane: reversedEndHipFramingPlane,
            isLeftSide: false,
          });
        });
        rightOrigin.forEach((position) => {
          generateEndHipJackRafter({
            origin: position,
            plane: endHipFramingPlane.plane,
            isLeftSide: true,
          });
        });
        if (middleOrigin) {
          generateEndHipJackRafter({
            origin: middleOrigin,
            plane: endHipFramingPlane.plane,
            isLeftSide: true,
            rafterPlaneOffset: -framingPlaneThickness / 2,
          });
        }
      }
    }
  }
}

function computeJackRafterPositions(
  startX: number,
  startY: number,
  startZ: number,
  endX: number,
  endY: number,
  endZ: number,
  roofFramingSpacing: number,
  addMiddlePoint: boolean = true,
): { left: Vec3[]; right: Vec3[]; middle: Vec3 | undefined } {
  if (roofFramingSpacing < 1) {
    throw new Error("roofFramingSpacing is too small"); //should not happen as we cap this earlier after user input but what if some intermediate calc changes this value
  }
  const length = vec3Dist(startX, startY, startZ, endX, endY, endZ);
  const [normalX, normalY, normalZ] = vec3Normalize(endX - startX, endY - startY, endZ - startZ);

  const leftPositions: Vec3[] = [];
  const rightPositions: Vec3[] = [];
  let middlePosition: Vec3 | undefined;

  leftPositions.push([startX, startY, startZ]);
  rightPositions.push([endX, endY, endZ]);

  if (addMiddlePoint) {
    const middleX = (startX + endX) / 2;
    const middleY = (startY + endY) / 2;
    const middleZ = (startZ + endZ) / 2;
    middlePosition = [middleX, middleY, middleZ];
  }

  let currentPosition = roofFramingSpacing;

  while (currentPosition < length / 2 - DIST_EPSILON) {
    const nextLeftX = startX + normalX * currentPosition;
    const nextLeftY = startY + normalY * currentPosition;
    const nextLeftZ = startZ + normalZ * currentPosition;
    leftPositions.push([nextLeftX, nextLeftY, nextLeftZ]);

    const nextRightX = endX - normalX * currentPosition;
    const nextRightY = endY - normalY * currentPosition;
    const nextRightZ = endZ - normalZ * currentPosition;
    rightPositions.push([nextRightX, nextRightY, nextRightZ]);

    currentPosition += roofFramingSpacing;

    if (length / 2 - currentPosition < 0.5 * roofFramingSpacing) {
      break;
    }
  }

  return { left: leftPositions, right: rightPositions, middle: middlePosition };
}

interface JackRafterParams {
  framingPlanes: GeneratedRoofFramingPlane[];
  jackRafterProfile: LumberProfileType;
  ridgeRailsAtPeak: RidgeRailAtPeakParams;
  roofSlopes: RoofSlopesParams;
  leftSpan: number;
  rightSpan: number;
  peakRailExtendedNorm: [number, number, number];
  fasciaSizes: FasciaSizeParams;
}

export function generateJackRafters(
  framingPlane: GeneratedRoofFramingPlane,
  index: number,
  roofFraming: RoofFraming[],
  {
    framingPlanes,
    jackRafterProfile,
    ridgeRailsAtPeak,
    roofSlopes,
    leftSpan,
    rightSpan,
    peakRailExtendedNorm,
    fasciaSizes,
  }: JackRafterParams,
) {
  const { plane: trussPlane, type: regionType } = framingPlane;

  //current index origin
  const { originX: indexOriginX, originY: indexOriginY, originZ: indexOriginZ } = trussPlane;

  if (regionType !== RoofFramingPlaneType.HIP_FRAMING_PLANE) {
    return;
  }

  const {
    firstPeakFramingPlaneIndex,
    lastPeakFramingPlaneIndex,
    leftRidgeRailAtFirstPeak,
    rightRidgeRailAtFirstPeak,
    leftRidgeRailAtLastPeak,
    rightRidgeRailAtLastPeak,
  } = ridgeRailsAtPeak;

  const { leftTopChordPitch, rightTopChordPitch, startHipSideSlope, endHipSideSlope } = roofSlopes;
  const { startEaveFasciaSize, endEaveFasciaSize, leftEaveFasciaSize, rightEaveFasciaSize } = fasciaSizes;
  if (firstPeakFramingPlaneIndex && index < firstPeakFramingPlaneIndex && index !== 0) {
    const peakPlaneStart = framingPlanes[firstPeakFramingPlaneIndex].plane;
    const firstPeakFramingPlaneOrigin = {
      x: peakPlaneStart.originX,
      y: peakPlaneStart.originY,
      z: peakPlaneStart.originZ,
    };
    const { originX: startOriginX, originY: startOriginY, originZ: startOriginZ } = framingPlanes[0].plane;

    const commonParams = {
      peakOrigin: firstPeakFramingPlaneOrigin,
      indexOrigin: { x: indexOriginX, y: indexOriginY },
      trussPlane,
      peakRailExtendedNorm,
    };

    const perpendicularSpan = vec3Dist(
      startOriginX,
      startOriginY,
      startOriginZ,
      indexOriginX,
      indexOriginY,
      indexOriginZ,
    );

    if (leftRidgeRailAtFirstPeak) {
      const { span: leftHipSpan, intersectionVec3: intersectionVecLeftRidgeRail } = calculateHipSpan({
        ...commonParams,
        ridgeRail: leftRidgeRailAtFirstPeak,
      });

      if (intersectionVecLeftRidgeRail && leftHipSpan) {
        //todo generate jack rafter
        const [jackRafterOriginX, jackRafterOriginY, jackRafterOriginZ] = intersectionVecLeftRidgeRail;

        generateJackRafterAt(
          {
            x: jackRafterOriginX,
            y: jackRafterOriginY,
            z: jackRafterOriginZ,
          },
          leftSpan - leftHipSpan,
          leftTopChordPitch,
          trussPlane,
          false,
          true,
          jackRafterProfile,
          leftEaveFasciaSize,
          0,
          roofFraming,
        );

        generateJackRafterAt(
          {
            x: jackRafterOriginX,
            y: jackRafterOriginY,
            z: jackRafterOriginZ,
          },
          perpendicularSpan,
          startHipSideSlope ?? leftTopChordPitch ?? rightTopChordPitch,
          planeCopyReversed(trussPlane),
          true,
          true,
          jackRafterProfile,
          startEaveFasciaSize,
          0,
          roofFraming,
        );
      }
    }
    if (rightRidgeRailAtFirstPeak) {
      const { span: rightHipSpan, intersectionVec3: intersectionVecRightRidgeRail } = calculateHipSpan({
        ...commonParams,
        ridgeRail: rightRidgeRailAtFirstPeak,
      });

      if (intersectionVecRightRidgeRail && rightHipSpan) {
        const [jackRafterOriginX, jackRafterOriginY, jackRafterOriginZ] = intersectionVecRightRidgeRail;

        generateJackRafterAt(
          {
            x: jackRafterOriginX,
            y: jackRafterOriginY,
            z: jackRafterOriginZ,
          },
          rightSpan - rightHipSpan,
          rightTopChordPitch,
          trussPlane,
          false,
          false,
          jackRafterProfile,
          rightEaveFasciaSize,
          0,
          roofFraming,
        );

        generateJackRafterAt(
          {
            x: jackRafterOriginX,
            y: jackRafterOriginY,
            z: jackRafterOriginZ,
          },
          perpendicularSpan,
          startHipSideSlope ?? leftTopChordPitch ?? rightTopChordPitch,
          trussPlane,
          true,
          false,
          jackRafterProfile,
          startEaveFasciaSize,
          0,
          roofFraming,
        );
      }
    }
  }

  if (lastPeakFramingPlaneIndex && index > lastPeakFramingPlaneIndex && index !== framingPlanes.length - 1) {
    const peakPlaneEnd = framingPlanes[lastPeakFramingPlaneIndex].plane;
    const lastPeakFramingPlaneOrigin = {
      x: peakPlaneEnd.originX,
      y: peakPlaneEnd.originY,
      z: peakPlaneEnd.originZ,
    };

    const {
      originX: endOriginX,
      originY: endOriginY,
      originZ: endOriginZ,
    } = framingPlanes[framingPlanes.length - 1].plane;

    const commonParams = {
      peakOrigin: lastPeakFramingPlaneOrigin,
      indexOrigin: { x: indexOriginX, y: indexOriginY },
      trussPlane,
      peakRailExtendedNorm,
    };

    const perpendicularSpan = vec3Dist(endOriginX, endOriginY, endOriginZ, indexOriginX, indexOriginY, indexOriginZ);

    if (leftRidgeRailAtLastPeak) {
      const { span: leftHipSpan, intersectionVec3: intersectionVecLeftRidgeRail } = calculateHipSpan({
        ...commonParams,
        ridgeRail: leftRidgeRailAtLastPeak,
      });
      if (intersectionVecLeftRidgeRail && leftHipSpan) {
        const [jackRafterOriginX, jackRafterOriginY, jackRafterOriginZ] = intersectionVecLeftRidgeRail;

        generateJackRafterAt(
          {
            x: jackRafterOriginX,
            y: jackRafterOriginY,
            z: jackRafterOriginZ,
          },
          leftSpan - leftHipSpan,
          leftTopChordPitch,
          trussPlane,
          false,
          true,
          jackRafterProfile,
          leftEaveFasciaSize,
          0,
          roofFraming,
        );

        generateJackRafterAt(
          {
            x: jackRafterOriginX,
            y: jackRafterOriginY,
            z: jackRafterOriginZ,
          },
          perpendicularSpan,
          endHipSideSlope ?? leftTopChordPitch ?? rightTopChordPitch,
          planeCopyReversed(trussPlane),
          true,
          false,
          jackRafterProfile,
          endEaveFasciaSize,
          0,
          roofFraming,
        );
      }
    }
    if (rightRidgeRailAtLastPeak) {
      const { span: rightHipSpan, intersectionVec3: intersectionVecRightRidgeRail } = calculateHipSpan({
        ...commonParams,
        ridgeRail: rightRidgeRailAtLastPeak,
      });
      if (intersectionVecRightRidgeRail && rightHipSpan) {
        const [jackRafterOriginX, jackRafterOriginY, jackRafterOriginZ] = intersectionVecRightRidgeRail;

        generateJackRafterAt(
          {
            x: jackRafterOriginX,
            y: jackRafterOriginY,
            z: jackRafterOriginZ,
          },
          rightSpan - rightHipSpan,
          rightTopChordPitch,
          trussPlane,
          false,
          false,
          jackRafterProfile,
          rightEaveFasciaSize,
          0,
          roofFraming,
        );

        generateJackRafterAt(
          {
            x: jackRafterOriginX,
            y: jackRafterOriginY,
            z: jackRafterOriginZ,
          },
          perpendicularSpan,
          endHipSideSlope ?? leftTopChordPitch ?? rightTopChordPitch,
          trussPlane,
          true,
          true,
          jackRafterProfile,
          endEaveFasciaSize,
          0,
          roofFraming,
        );
      }
    }
  }
}

export function generateJackRafterAt(
  jackRafterOrigin: { x: number; y: number; z: number },
  rafterHorizontalSpan: number,
  rafterPitch: number,
  plane: Plane,
  isPerpendicular: boolean,
  isLeftSide: boolean,
  rafterProfile: LumberProfileType,
  fasciaSize: number,
  rafterPlaneOffset: number,
  roofFraming: RoofFraming[],
) {
  const [normalX, normalY, normalZ] = planeNormal(plane);
  let rafterPlane: Plane | undefined;

  if (isPerpendicular) {
    rafterPlane = planeMoveAlongNormal(
      planeNew(jackRafterOrigin.x, jackRafterOrigin.y, jackRafterOrigin.z, normalX, normalY, normalZ, 0, 0, 1),
      rafterPlaneOffset,
    );
  } else {
    rafterPlane = planeNew(
      jackRafterOrigin.x,
      jackRafterOrigin.y,
      jackRafterOrigin.z,
      plane.uX,
      plane.uY,
      plane.uZ,
      plane.vX,
      plane.vY,
      plane.vZ,
    );
  }
  const jackRafter = generateJackRafter(
    unsignedNumberNew(rafterHorizontalSpan),
    unsignedNumberNew(rafterPitch),
    rafterProfile,
    rafterPlane,
    unsignedNumberNew(fasciaSize),
    isLeftSide,
  );
  roofFraming.push(jackRafter);
}
